const NY_TZ = "America/New_York";

const isoFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: NY_TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const displayFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: NY_TZ,
  weekday: "short",
  month: "short",
  day: "numeric",
});

const monthDayFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: NY_TZ,
  month: "short",
  day: "numeric",
});

const monthHeaderFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: NY_TZ,
  month: "long",
  year: "numeric",
});

export type IsoDate = string;

export function todayISO(now: Date = new Date()): IsoDate {
  return isoFormatter.format(now);
}

export function parseISO(s: IsoDate): Date {
  // Interpret as local midnight. Display uses NY tz so the output is stable
  // regardless of the browser's local tz.
  const [y, m, d] = s.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
}

export function toISO(d: Date): IsoDate {
  return isoFormatter.format(d);
}

export function addDays(s: IsoDate, delta: number): IsoDate {
  const d = parseISO(s);
  d.setUTCDate(d.getUTCDate() + delta);
  return toISO(d);
}

export function isSameDay(a: IsoDate, b: IsoDate): boolean {
  return a === b;
}

export function formatDisplay(s: IsoDate): string {
  return displayFormatter.format(parseISO(s));
}

export function formatMonthDay(s: IsoDate): string {
  return monthDayFormatter.format(parseISO(s));
}

export function formatMonthHeader(s: IsoDate): string {
  return monthHeaderFormatter.format(parseISO(s));
}

export function firstOfMonth(s: IsoDate): IsoDate {
  return `${s.slice(0, 7)}-01`;
}

export function addMonths(s: IsoDate, delta: number): IsoDate {
  const [y, m] = s.split("-").map(Number) as [number, number, number];
  const raw = (y * 12 + (m - 1) + delta) | 0;
  const ny = Math.floor(raw / 12);
  const nm = (raw % 12) + 1;
  return `${String(ny).padStart(4, "0")}-${String(nm).padStart(2, "0")}-01`;
}

export function monthGrid(anchor: IsoDate): IsoDate[] {
  const first = firstOfMonth(anchor);
  const firstDate = parseISO(first);
  const weekday = firstDate.getUTCDay();
  const [y, m] = first.split("-").map(Number) as [number, number, number];
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const start = addDays(first, -weekday);
  const totalCells = Math.ceil((weekday + daysInMonth) / 7) * 7;
  const out: IsoDate[] = [];
  for (let i = 0; i < totalCells; i++) out.push(addDays(start, i));
  return out;
}

export function monthOf(s: IsoDate): string {
  return s.slice(0, 7);
}
